import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Plus, Trash2, FileDown, Upload, FileText, ReceiptText, Check, Send } from "lucide-react";
import { toast } from "sonner";
import { CURRENCIES, fmtMoney } from "../../lib/currency";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};
const lbl = "text-xs uppercase tracking-wider text-[#5C5651]";
const fieldCls = "mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]";
const EMPTY_PRODUCT = { item: "", cost: "", markup: "" };

function downloadInvoice(invId, fallback) {
  return api.get(`/invoices/${invId}/download`, { responseType: "blob" }).then((res) => {
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    const cd = res.headers["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/);
    a.download = m ? m[1] : fallback;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  });
}

function InvoiceForm({ projectId, project, onCreated, onClose }) {
  const { user } = useAuth();
  const [f, setF] = useState({
    business_name: user?.business_name || "",
    business_address: user?.company_address || "",
    business_phone: user?.company_phone || "",
    business_email: user?.company_email || user?.email || "",
    bill_to_name: project?.client_name || project?.client_email || "",
    bill_to_info: "",
    date: new Date().toISOString().slice(0, 10),
    invoice_number: "",
    project_reference: project?.name || "",
    billing_method: "flat",
    design_fees: "",
    billable_hours: "",
    tax_rate: "",
    payment_terms: "",
    notes: "",
    currency: "CAD",
  });
  const [products, setProducts] = useState([{ ...EMPTY_PRODUCT }]);
  const [subtotalEdit, setSubtotalEdit] = useState("");
  const [stEdited, setStEdited] = useState(false);
  const [grandEdit, setGrandEdit] = useState("");
  const [gtEdited, setGtEdited] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setProduct = (i, k, v) => setProducts((p) => p.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addProduct = () => setProducts((p) => [...p, { ...EMPTY_PRODUCT }]);
  const removeProduct = (i) => setProducts((p) => p.filter((_, idx) => idx !== i));

  const servicesTotal = useMemo(() => {
    const fee = Number(f.design_fees) || 0;
    return f.billing_method === "hourly" ? fee * (Number(f.billable_hours) || 0) : fee;
  }, [f.design_fees, f.billing_method, f.billable_hours]);

  const productsTotal = useMemo(
    () => products.reduce((s, p) => s + (Number(p.cost) || 0) * (1 + (Number(p.markup) || 0) / 100), 0),
    [products]
  );

  const computedSubtotal = servicesTotal + productsTotal;
  const subtotal = stEdited ? Number(subtotalEdit) || 0 : computedSubtotal;
  const taxAmt = subtotal * (Number(f.tax_rate) || 0) / 100;
  const computedGrand = subtotal + taxAmt;
  const grandTotal = gtEdited ? Number(grandEdit) || 0 : computedGrand;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...f,
        design_fees: Number(f.design_fees) || 0,
        billable_hours: Number(f.billable_hours) || 0,
        tax_rate: Number(f.tax_rate) || 0,
        products: products
          .filter((p) => p.item.trim() || p.cost !== "")
          .map((p) => ({ item: p.item.trim(), cost: Number(p.cost) || 0, markup: Number(p.markup) || 0 })),
        subtotal,
        grand_total: grandTotal,
      };
      const { data } = await api.post(`/projects/${projectId}/invoices`, payload);
      toast.success("Invoice created");
      onCreated(data);
      onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const Section = ({ title, children }) => (
    <div className="border border-[#EBE5E0] p-4">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#A68A6B] mb-3">{title}</p>
      {children}
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-4 mt-2" data-testid="invoice-form">
      <Section title="General">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className={lbl}>Business name</Label>
            <Input data-testid="inv-business-name" value={f.business_name} onChange={(e) => set("business_name", e.target.value)} className={fieldCls} /></div>
          <div><Label className={lbl}>Business email</Label>
            <Input data-testid="inv-business-email" value={f.business_email} onChange={(e) => set("business_email", e.target.value)} className={fieldCls} /></div>
          <div><Label className={lbl}>Business phone</Label>
            <Input data-testid="inv-business-phone" value={f.business_phone} onChange={(e) => set("business_phone", e.target.value)} className={fieldCls} /></div>
          <div><Label className={lbl}>Business address</Label>
            <Input data-testid="inv-business-address" value={f.business_address} onChange={(e) => set("business_address", e.target.value)} className={fieldCls} /></div>
          <div><Label className={lbl}>Client / vendor name</Label>
            <Input data-testid="inv-billto-name" value={f.bill_to_name} onChange={(e) => set("bill_to_name", e.target.value)} className={fieldCls} /></div>
          <div><Label className={lbl}>Client / vendor info</Label>
            <Input data-testid="inv-billto-info" value={f.bill_to_info} onChange={(e) => set("bill_to_info", e.target.value)} placeholder="address / email / phone" className={fieldCls} /></div>
          <div><Label className={lbl}>Date</Label>
            <Input data-testid="inv-date" type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className={fieldCls} /></div>
          <div><Label className={lbl}>Invoice number</Label>
            <Input data-testid="inv-number" value={f.invoice_number} onChange={(e) => set("invoice_number", e.target.value)} placeholder="e.g. 1042" className={fieldCls} /></div>
          <div className="sm:col-span-2"><Label className={lbl}>Project name / reference</Label>
            <Input data-testid="inv-project-ref" value={f.project_reference} onChange={(e) => set("project_reference", e.target.value)} className={fieldCls} /></div>
          <div><Label className={lbl}>Currency</Label>
            <Select value={f.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger data-testid="inv-currency" className="mt-1 rounded-none border-[#EBE5E0] focus:ring-0"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Section>

      <Section title="Services">
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div>
            <Label className={lbl}>Billing method</Label>
            <Select value={f.billing_method} onValueChange={(v) => set("billing_method", v)}>
              <SelectTrigger data-testid="inv-billing-method" className="mt-1 rounded-none border-[#EBE5E0] focus:ring-0"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="flat">Flat rate</SelectItem>
                <SelectItem value="hourly">Hourly</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className={lbl}>{f.billing_method === "hourly" ? "Hourly rate" : "Design fee (flat)"}</Label>
            <Input data-testid="inv-design-fees" type="number" step="0.01" value={f.design_fees} onChange={(e) => set("design_fees", e.target.value)} className={fieldCls} />
          </div>
          {f.billing_method === "hourly" && (
            <div>
              <Label className={lbl}>Billable hours</Label>
              <Input data-testid="inv-hours" type="number" step="0.25" value={f.billable_hours} onChange={(e) => set("billable_hours", e.target.value)} className={fieldCls} />
            </div>
          )}
        </div>
        <p className="text-[11px] text-[#9b938b] mt-2">Services subtotal: <span className="text-[#8C7154]">{fmtMoney(servicesTotal, f.currency)}</span></p>
      </Section>

      <Section title="Products & Materials">
        <div className="space-y-2">
          <div className="hidden sm:grid grid-cols-[1fr_110px_90px_100px_28px] gap-2 text-[10px] uppercase tracking-wider text-[#9b938b]">
            <span>Item</span><span>Cost / item</span><span>Markup %</span><span className="text-right">Amount</span><span />
          </div>
          {products.map((p, i) => {
            const amt = (Number(p.cost) || 0) * (1 + (Number(p.markup) || 0) / 100);
            return (
              <div key={i} className="grid grid-cols-[1fr_110px_90px_100px_28px] gap-2 items-center" data-testid={`inv-product-row-${i}`}>
                <Input data-testid={`inv-product-item-${i}`} value={p.item} onChange={(e) => setProduct(i, "item", e.target.value)} placeholder="Item" className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                <Input data-testid={`inv-product-cost-${i}`} type="number" step="0.01" value={p.cost} onChange={(e) => setProduct(i, "cost", e.target.value)} placeholder="0.00" className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                <Input data-testid={`inv-product-markup-${i}`} type="number" step="0.1" value={p.markup} onChange={(e) => setProduct(i, "markup", e.target.value)} placeholder="0" className="rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
                <span className="text-sm text-[#2B2622] text-right">{fmtMoney(amt, f.currency)}</span>
                <button type="button" onClick={() => removeProduct(i)} className="text-[#5C5651] hover:text-[#9E4747]"><Trash2 size={15} /></button>
              </div>
            );
          })}
          <Button type="button" variant="outline" onClick={addProduct} data-testid="inv-add-product"
            className="rounded-none border-[#A68A6B] text-[#8C7154] hover:bg-[#F2EFEB] uppercase tracking-wider text-xs">
            <Plus size={14} className="mr-1.5" /> Add item
          </Button>
        </div>
      </Section>

      <Section title="Totals & Payment Terms">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className={lbl}>Subtotal</Label>
            <Input data-testid="inv-subtotal" type="number" step="0.01"
              value={stEdited ? subtotalEdit : computedSubtotal.toFixed(2)}
              onChange={(e) => { setStEdited(true); setSubtotalEdit(e.target.value); }} className={fieldCls} />
            <p className="text-[10px] text-[#9b938b] mt-0.5">Auto-calculated — editable</p>
          </div>
          <div>
            <Label className={lbl}>Tax rate (%)</Label>
            <Input data-testid="inv-tax-rate" type="number" step="0.01" value={f.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} placeholder="e.g. 13" className={fieldCls} />
            <p className="text-[10px] text-[#9b938b] mt-0.5">Tax: {fmtMoney(taxAmt, f.currency)}</p>
          </div>
          <div>
            <Label className={lbl}>Grand total</Label>
            <Input data-testid="inv-grand-total" type="number" step="0.01"
              value={gtEdited ? grandEdit : computedGrand.toFixed(2)}
              onChange={(e) => { setGtEdited(true); setGrandEdit(e.target.value); }} className={fieldCls} />
            <p className="text-[10px] text-[#9b938b] mt-0.5">Auto-calculated — editable</p>
          </div>
          <div>
            <Label className={lbl}>Payment terms</Label>
            <Input data-testid="inv-payment-terms" value={f.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} placeholder="e.g. Net 30" className={fieldCls} />
          </div>
          <div className="sm:col-span-2">
            <Label className={lbl}>Notes (optional)</Label>
            <Textarea data-testid="inv-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} className={fieldCls} />
          </div>
        </div>
      </Section>

      <Button type="submit" disabled={busy} data-testid="inv-create-submit"
        className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
        {busy ? "Creating…" : "Create Invoice"}
      </Button>
    </form>
  );
}

export default function InvoicesTab({ projectId, project }) {
  const { user } = useAuth();
  const isDesigner = user?.role === "designer";
  const [invoices, setInvoices] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [emailFor, setEmailFor] = useState(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailMsg, setEmailMsg] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/projects/${projectId}/invoices`).then((r) => setInvoices(r.data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]);

  const dashboard = useMemo(() => {
    const byCur = {};
    const now = new Date();
    let hasData = false;
    for (const inv of invoices) {
      if (!(inv.kind === "created" || inv.grand_total)) continue;
      hasData = true;
      const cur = inv.currency || "CAD";
      const total = Number(inv.grand_total) || 0;
      if (!byCur[cur]) byCur[cur] = { paid: 0, outstanding: 0, overdue: 0, overdueCount: 0 };
      if (inv.paid) { byCur[cur].paid += total; continue; }
      byCur[cur].outstanding += total;
      const net = (inv.payment_terms || "").match(/(\d+)/);
      const base = inv.date || inv.created_at;
      if (net && base) {
        const due = new Date(base);
        due.setDate(due.getDate() + parseInt(net[1], 10));
        if (now > due) { byCur[cur].overdue += total; byCur[cur].overdueCount += 1; }
      }
    }
    return { byCur, hasData };
  }, [invoices]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label);
      const { data } = await api.post(`/projects/${projectId}/invoices/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setInvoices((p) => [data, ...p]);
      setFile(null); setLabel(""); setUploadOpen(false);
      toast.success("Invoice uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => { await api.delete(`/invoices/${id}`); setInvoices((p) => p.filter((x) => x.id !== id)); };

  const togglePaid = async (inv) => {
    try {
      const { data } = await api.put(`/invoices/${inv.id}/paid`, { paid: !inv.paid, paid_date: inv.paid ? "" : new Date().toISOString().slice(0, 10) });
      setInvoices((p) => p.map((x) => (x.id === inv.id ? data : x)));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const download = async (inv) => {
    setDownloading(inv.id);
    try { await downloadInvoice(inv.id, `Invoice-${inv.invoice_number || inv.title || "invoice"}.pdf`); }
    catch { toast.error("Could not download invoice"); }
    finally { setDownloading(null); }
  };

  const openEmail = (inv) => {
    setEmailFor(inv);
    setEmailTo(project?.client_email || "");
    setEmailMsg("");
  };

  const isOverdue = (inv) => {
    if (inv.paid) return false;
    const net = (inv.payment_terms || "").match(/(\d+)/);
    const base = inv.date || inv.created_at;
    if (!net || !base) return false;
    const due = new Date(base); due.setDate(due.getDate() + parseInt(net[1], 10));
    return new Date() > due;
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/invoices/${emailFor.id}/email`, { to: emailTo, message: emailMsg });
      toast.success(`Invoice sent to ${data.to}`);
      setEmailFor(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-5xl space-y-6" data-testid="invoices-tab">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A68A6B] mb-1">Billing</p>
          <h3 className="font-serif text-3xl text-[#2B2622]">Invoices</h3>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-invoice-btn"
                className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5 px-5">
                <Plus size={15} className="mr-2" /> Create Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-[#EBE5E0] bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#2B2622]">New invoice</DialogTitle>
                <DialogDescription className="text-sm text-[#5C5651]">Your studio branding is added to the top automatically. Blank fields are omitted from the PDF.</DialogDescription>
              </DialogHeader>
              <InvoiceForm projectId={projectId} project={project} onClose={() => setCreateOpen(false)}
                onCreated={(data) => setInvoices((p) => [data, ...p])} />
            </DialogContent>
          </Dialog>

          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button data-testid="upload-invoice-btn" variant="outline"
                className="rounded-none border-[#A68A6B] text-[#8C7154] hover:bg-[#F2EFEB] uppercase tracking-wider text-xs py-5 px-5">
                <Upload size={15} className="mr-2" /> Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none border-[#EBE5E0] bg-white">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-[#2B2622]">Upload invoice</DialogTitle>
                <DialogDescription className="sr-only">Upload an invoice document</DialogDescription>
              </DialogHeader>
              <form onSubmit={upload} className="space-y-4 mt-2">
                <div>
                  <Label className={lbl}>Label (optional)</Label>
                  <Input data-testid="invoice-label-input" value={label} onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g. Invoice #1042" className={fieldCls} />
                </div>
                <div>
                  <Label className={lbl}>File (PDF or image)</Label>
                  <input data-testid="invoice-file-input" type="file" accept="application/pdf,image/*"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="mt-1 block w-full text-sm text-[#5C5651] file:mr-3 file:border file:border-[#A68A6B] file:bg-transparent file:text-[#A68A6B] file:px-4 file:py-2 file:uppercase file:text-xs file:tracking-wider" />
                  {file && <p className="text-[11px] text-[#9b938b] mt-1 truncate">{file.name}</p>}
                </div>
                <Button data-testid="invoice-upload-submit" type="submit" disabled={busy || !file}
                  className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
                  {busy ? "Uploading…" : "Upload"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {dashboard.hasData && (
        <div className="border border-[#EBE5E0] bg-white" data-testid="invoice-dashboard">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[#A68A6B] px-5 pt-4">Cash flow</p>
          <div className="divide-y divide-[#F2EFEB]">
            {Object.entries(dashboard.byCur).map(([cur, d]) => (
              <div key={cur} className="grid grid-cols-2 sm:grid-cols-4 gap-4 px-5 py-4" data-testid={`invoice-dashboard-${cur}`}>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#9b938b]">Currency</p>
                  <p className="text-lg font-serif text-[#2B2622]">{cur}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#9b938b]">Paid</p>
                  <p className="text-lg font-serif text-[#506B52]" data-testid={`dash-paid-${cur}`}>{fmtMoney(d.paid, cur)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#9b938b]">Outstanding</p>
                  <p className="text-lg font-serif text-[#8C7154]" data-testid={`dash-outstanding-${cur}`}>{fmtMoney(d.outstanding, cur)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-[#9b938b]">Overdue</p>
                  <p className={`text-lg font-serif ${d.overdue > 0 ? "text-[#9E4747]" : "text-[#9b938b]"}`} data-testid={`dash-overdue-${cur}`}>
                    {fmtMoney(d.overdue, cur)}{d.overdueCount > 0 ? ` · ${d.overdueCount}` : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="border border-dashed border-[#D9CBBF] bg-white p-12 text-center text-[#5C5651]">
          <ReceiptText size={28} strokeWidth={1.25} className="mx-auto mb-3 text-[#C9BBAE]" />
          No invoices yet. Create an invoice or upload an existing document.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-px bg-[#EBE5E0] border border-[#EBE5E0]">
          {invoices.map((inv) => (
            <div key={inv.id} className="bg-white p-5 flex flex-col" data-testid={`invoice-card-${inv.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={18} strokeWidth={1.5} className="text-[#A68A6B] shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm text-[#2B2622] font-medium truncate">{inv.title || `Invoice ${inv.invoice_number || ""}`}</p>
                    <p className="text-[11px] uppercase tracking-wider text-[#A68A6B]">
                      {fmtDate(inv.created_at)}
                      {inv.kind === "created" && inv.grand_total ? ` · ${fmtMoney(inv.grand_total, inv.currency)}` : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!inv.paid && isOverdue(inv) && (
                    <span data-testid={`invoice-overdue-badge-${inv.id}`}
                      className="inline-flex items-center text-[10px] uppercase tracking-wider bg-[#9E4747] text-white px-1.5 py-0.5">
                      Overdue
                    </span>
                  )}
                  {inv.paid && (
                    <span data-testid={`invoice-paid-badge-${inv.id}`}
                      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#506B52] text-white px-1.5 py-0.5">
                      <Check size={11} strokeWidth={3} /> Paid
                    </span>
                  )}
                  <button data-testid={`delete-invoice-${inv.id}`} onClick={() => remove(inv.id)}
                    className="text-[#5C5651] hover:text-[#9E4747]"><Trash2 size={15} strokeWidth={1.5} /></button>
                </div>
              </div>

              {inv.sent_to && <p className="text-[11px] text-[#9b938b] mt-2">Sent to {inv.sent_to}</p>}

              <div className="mt-4 pt-3 border-t border-[#F2EFEB] flex flex-wrap items-center gap-2">
                <button data-testid={`download-invoice-${inv.id}`} onClick={() => download(inv)} disabled={downloading === inv.id}
                  className="inline-flex items-center gap-1.5 border border-[#A68A6B] text-[#A68A6B] hover:bg-[#A68A6B] hover:text-white transition-colors px-3 py-2 text-[11px] uppercase tracking-wider disabled:opacity-60">
                  <FileDown size={14} strokeWidth={1.5} />
                  {downloading === inv.id ? "Preparing…" : "PDF"}
                </button>
                <button data-testid={`email-invoice-${inv.id}`} onClick={() => openEmail(inv)}
                  className="inline-flex items-center gap-1.5 border border-[#A68A6B] text-[#A68A6B] hover:bg-[#A68A6B] hover:text-white transition-colors px-3 py-2 text-[11px] uppercase tracking-wider">
                  <Send size={13} strokeWidth={1.5} /> Send
                </button>
                <button data-testid={`toggle-paid-invoice-${inv.id}`} onClick={() => togglePaid(inv)}
                  className={`inline-flex items-center gap-1.5 border px-3 py-2 text-[11px] uppercase tracking-wider transition-colors ${inv.paid ? "border-[#EBE5E0] text-[#5C5651] hover:border-[#A68A6B]" : "border-[#506B52] text-[#3f5641] hover:bg-[#506B52] hover:text-white"}`}>
                  <Check size={13} strokeWidth={2} /> {inv.paid ? "Mark unpaid" : "Mark paid"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!emailFor} onOpenChange={(o) => { if (!o) setEmailFor(null); }}>
        <DialogContent className="rounded-none border-[#EBE5E0] bg-white" data-testid="email-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#2B2622]">Send invoice</DialogTitle>
            <DialogDescription className="text-sm text-[#5C5651]">The invoice PDF will be emailed as an attachment.</DialogDescription>
          </DialogHeader>
          <form onSubmit={sendEmail} className="space-y-4 mt-2">
            <div>
              <Label className={lbl}>Recipient email</Label>
              <Input data-testid="email-invoice-to" type="email" required value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                placeholder="client@email.com" className={fieldCls} />
            </div>
            <div>
              <Label className={lbl}>Message (optional)</Label>
              <Textarea data-testid="email-invoice-message" value={emailMsg} onChange={(e) => setEmailMsg(e.target.value)} className={fieldCls} />
            </div>
            <Button type="submit" disabled={sending} data-testid="email-invoice-submit"
              className="w-full rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-5">
              {sending ? "Sending…" : "Send Invoice"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
